import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaTimes,
  FaHome,
  FaSync,
  FaDollarSign,
  FaChartLine,
  FaSearch,
  FaExchangeAlt,
  FaCog,
  FaNetworkWired,
  FaBell,
  FaMapMarkerAlt,
  FaUserFriends,
  FaShieldAlt,
  FaComments,
  FaTrophy,
} from 'react-icons/fa';
import './AdminDrawer.scss';

interface MenuItem {
  id: string;
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
}

interface AdminDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Admin Drawer Component
 * Searchable side navigation for admin pages
 */
const AdminDrawer: React.FC<AdminDrawerProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');

  // Define menu items (easy to extend in the future)
  const menuItems: MenuItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      path: '/admin/dashboard',
      icon: FaHome,
      description: 'Admin home and overview',
    },
    {
      id: 'sync',
      label: 'Blockchain Sync',
      path: '/admin/sync',
      icon: FaSync,
      description: 'Manage blockchain synchronization',
    },
    {
      id: 'spotlight-pricing',
      label: 'Spotlight Pricing',
      path: '/dashboard-admin/spotlight-pricing',
      icon: FaDollarSign,
      description: 'Configure spotlight pricing',
    },
    {
      id: 'spotlight-analytics',
      label: 'Spotlight Analytics',
      path: '/dashboard-admin/spotlight-analytics',
      icon: FaChartLine,
      description: 'View spotlight metrics',
    },
    {
      id: 'spotlight-transactions',
      label: 'Spotlight Transactions',
      path: '/dashboard-admin/spotlight-transactions',
      icon: FaExchangeAlt,
      description: 'Track spotlight transactions',
    },
    {
      id: 'network-fees',
      label: 'Network Fees',
      path: '/dashboard-admin/network-fees',
      icon: FaNetworkWired,
      description: 'Configure network gas fees',
    },
    {
      id: 'system-notifications',
      label: 'System Notifications',
      path: '/admin/system-notifications',
      icon: FaBell,
      description: 'Manage platform-wide banner messages',
    },
    {
      id: 'locations',
      label: 'Location Management',
      path: '/dashboard-admin/locations',
      icon: FaMapMarkerAlt,
      description: 'Bulk import and manage location data',
    },
    {
      id: 'chat-rooms',
      label: 'Chat Rooms',
      path: '/dashboard-admin/chat-rooms',
      icon: FaComments,
      description: 'Create and manage public chat rooms',
    },
    {
      id: 'user-growth',
      label: 'User Growth',
      path: '/admin/user-growth',
      icon: FaUserFriends,
      description: 'Sign-up funnel, auth method split, verification and profile creation rates',
    },
    {
      id: 'access-audit',
      label: 'Access Audit',
      path: '/admin/access-audit',
      icon: FaShieldAlt,
      description: 'Compare DB-cached vs on-chain profile access, detect and sync mismatches',
    },
    {
      id: 'achievement-earn',
      label: 'Achievement Earn',
      path: '/admin/achievement-earn',
      icon: FaTrophy,
      description: '@klyx-rewards KLV payouts for earned achievements — config, balance, ledger',
    },
    // Future menu items can be added here
    // {
    //   id: 'users',
    //   label: 'User Management',
    //   path: '/admin/users',
    //   icon: FaUsers,
    //   description: 'Manage users and permissions',
    // },
  ];

  // Filter menu items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return menuItems;
    }

    const query = searchQuery.toLowerCase();
    return menuItems.filter(
      (item) =>
        item.label.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
    setSearchQuery(''); // Clear search when navigating
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <>
      {/* Overlay (Mobile only) */}
      {isOpen && <div className="admin-drawer-overlay" onClick={onClose} />}

      {/* Drawer */}
      <aside className={`admin-drawer ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="admin-drawer-header">
          <div className="admin-drawer-title">
            <FaCog className="admin-icon" />
            <span>Admin Panel</span>
          </div>
          <button className="admin-drawer-close" onClick={onClose} aria-label="Close menu">
            <FaTimes />
          </button>
        </div>

        {/* Search Bar */}
        <div className="admin-drawer-search">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search admin features..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Menu Items */}
        <nav className="admin-drawer-nav">
          {filteredItems.length > 0 ? (
            <ul className="admin-menu-list">
              {filteredItems.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.id}>
                    <button
                      className={`admin-menu-item ${isActive(item.path) ? 'active' : ''}`}
                      onClick={() => handleNavigate(item.path)}
                    >
                      <Icon className="menu-icon" />
                      <div className="menu-content">
                        <span className="menu-label">{item.label}</span>
                        {item.description && (
                          <span className="menu-description">{item.description}</span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="admin-no-results">
              <p>No results found for "{searchQuery}"</p>
            </div>
          )}
        </nav>
      </aside>
    </>
  );
};

export default AdminDrawer;
